import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { SettingsTabs } from '../../components/app-shell/SettingsTabs';
import { PhoneNumberInput } from '../../components/PhoneNumberInput';
import { MotionSection, PageHero, SectionTitle } from '../../components/ui';
import { ApiError, mutateJson } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { useFormTelemetry } from '../../lib/telemetry-form';
import { showToast } from '../../lib/toast';

// Mirrors the server-side pattern in worker/routes/verification-mobile.ts —
// keep the two in sync if the accepted shape ever changes. PhoneNumberInput
// emits this shape once a number is complete, so a mismatch here means
// "unfinished", not "wrong format".
const MOBILE_NUMBER_REGEX = /^\+[1-9]\d{6,14}$/;
const INCOMPLETE_NUMBER_MESSAGE = 'That number looks incomplete — check it and try again.';

/** Server detail when present (ApiError.message), else a generic fallback. */
function saveErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "We couldn't save that. Check your connection and try again.";
}

type AccountIdentity = {
  ok: boolean;
  account: {
    displayName: string;
    legalFullName: string | null;
    mobileNumber: string | null;
    mobileVerifiedAt: string | null;
    emergencyContactName: string | null;
    emergencyContactPhone: string | null;
  };
};

type ArtistMe = {
  ok: boolean;
  profile: {
    id: string;
    slug: string;
    bio: string | null;
    city: string | null;
  };
};

type VenueAccountMe = {
  ok: boolean;
  account: {
    id: string;
    display_name: string;
    billing_email: string;
  };
};

const TIMEZONES = [
  'Australia/Brisbane',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Adelaide',
  'Pacific/Auckland',
  'Asia/Singapore',
];

export const Route = createFileRoute('/settings/account')({
  component: AccountSettingsPage,
});

function AccountSettingsPage() {
  const { user, capabilities, activeHat, isLoading } = useAuth();
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: {
      display_name: '',
      timezone: 'Australia/Brisbane',
    },
  });

  const tel = useFormTelemetry('settings.profile', form);

  // Hydrate form once user resolves.
  useEffect(() => {
    if (!user) return;
    form.setValues({
      display_name: user.displayName,
      timezone: 'Australia/Brisbane',
    });
    // intentionally no form in deps — form's setValues is stable per Mantine
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Pull capability-shaped supplementary data so the page reflects every hat
  // the user wears — dual-role users see both cards.
  const artistQuery = useQuery({
    enabled: capabilities?.isArtist === true,
    queryKey: ['profiles', 'me', 'artist'],
    queryFn: async (): Promise<ArtistMe> => {
      const res = await fetch('/api/profiles/me/artist', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load artist profile');
      return res.json();
    },
  });

  const venueQuery = useQuery({
    enabled: capabilities?.isHost === true,
    queryKey: ['profiles', 'me', 'venue-account'],
    queryFn: async (): Promise<VenueAccountMe> => {
      const res = await fetch('/api/profiles/me/venue-account', {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load venue account');
      return res.json();
    },
  });

  const saveVenueAccount = useMutation({
    mutationFn: (values: { display_name: string }) =>
      mutateJson('/api/profiles/me/venue-account', values, { method: 'PATCH' }),
    onSuccess: () => {
      tel.trackSubmitSuccess();
      showToast({ kind: 'success', body: 'Account saved.' });
      queryClient.invalidateQueries({ queryKey: ['profiles', 'me', 'venue-account'] });
    },
    onError: (err: unknown) => {
      tel.trackSubmitFailure('save_failed');
      showToast({ kind: 'error', body: saveErrorMessage(err) });
    },
  });

  if (isLoading) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }

  if (!user) {
    return (
      <AppShell tone="form">
        <Text>Sign in to manage your account.</Text>
      </AppShell>
    );
  }

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Settings"
            title="Account"
            lede="Your basic details. Email, notifications, security, and privacy live under their own tabs."
          />
        </MotionSection>

        <SettingsTabs current="account" />

        <Card withBorder padding="lg" radius="md">
          <SectionTitle title="Profile basics" order={3} />
          <Box mt="md" />
          <form
            onSubmit={form.onSubmit((values) => {
              if (activeHat === 'host' && capabilities?.isHost) {
                saveVenueAccount.mutate({ display_name: values.display_name });
              } else {
                showToast({
                  kind: 'info',
                  body: 'Display-name editing for artists ships with the profile editor.',
                });
              }
            })}
          >
            <Stack gap="md">
              <TextInput
                label="Email"
                value={user.email}
                disabled
                description="Email changes go through support for now."
              />
              <TextInput
                label="Display name"
                {...form.getInputProps('display_name')}
                required
              />
              <Select
                label="Timezone"
                data={TIMEZONES}
                {...form.getInputProps('timezone')}
                description="Used for date and time displays across the app."
              />
              <Button type="submit" loading={saveVenueAccount.isPending}>
                Save changes
              </Button>
            </Stack>
          </form>
        </Card>

        {capabilities?.isArtist && artistQuery.data ? (
          <Card withBorder padding="lg">
            <Text fw={600}>Artist profile</Text>
            <Text size="sm" c="dimmed">
              Slug: <strong>{artistQuery.data.profile.slug}</strong>
            </Text>
            <Text size="sm" c="dimmed" mt="xs">
              Edit your artist bio, photos, and links from your profile editor.
            </Text>
          </Card>
        ) : null}

        {capabilities?.isHost && venueQuery.data ? (
          <Card withBorder padding="lg">
            <Text fw={600}>Billing email</Text>
            <Text size="sm" c="dimmed">
              {venueQuery.data.account.billing_email}
            </Text>
            <Text size="sm" c="dimmed" mt="xs">
              Update billing email from billing settings.
            </Text>
          </Card>
        ) : null}

        <IdentityCard />
      </Stack>
    </AppShell>
  );
}

function IdentityCard() {
  const queryClient = useQueryClient();
  const [verifyStep, setVerifyStep] = useState<'idle' | 'sent' | 'verifying'>('idle');
  // Preview mode (Twilio not configured + accept_any): the server sends no
  // text and accepts any 6-digit code — the copy below must say so instead
  // of telling the user to wait for an SMS that will never arrive.
  const [mockSend, setMockSend] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');

  const accountQuery = useQuery({
    queryKey: ['users', 'me', 'account'],
    queryFn: async (): Promise<AccountIdentity> => {
      const res = await fetch('/api/users/me/account', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load account');
      return res.json();
    },
  });

  const identityForm = useForm({
    initialValues: {
      legalFullName: '',
      mobileNumber: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
    },
    validateInputOnBlur: true,
    validate: {
      // PhoneNumberInput owns country-code handling and emits the strict
      // international form when the number is complete — so a failing regex
      // here means "not finished typing", not a format lesson. Blank is fine
      // (the fields are optional until verification / save).
      mobileNumber: (value) =>
        value.trim() !== '' && !MOBILE_NUMBER_REGEX.test(value.trim())
          ? INCOMPLETE_NUMBER_MESSAGE
          : null,
      emergencyContactPhone: (value) =>
        value.trim() !== '' && !MOBILE_NUMBER_REGEX.test(value.trim())
          ? INCOMPLETE_NUMBER_MESSAGE
          : null,
    },
  });

  useEffect(() => {
    if (!accountQuery.data) return;
    const a = accountQuery.data.account;
    identityForm.setValues({
      legalFullName: a.legalFullName ?? '',
      mobileNumber: a.mobileNumber ?? '',
      emergencyContactName: a.emergencyContactName ?? '',
      emergencyContactPhone: a.emergencyContactPhone ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountQuery.data]);

  const saveIdentity = useMutation({
    mutationFn: async (values: typeof identityForm.values) => {
      // FR-143: don't silently drop emptied fields. Trim every value and send
      // trimmed non-empty values as-is; when a previously-set field has been
      // cleared, send an explicit null so the clear round-trips instead of being
      // discarded on the client. Fields that are empty and were already empty are
      // omitted (nothing to change). Server-side validation stays the single
      // source of truth on what may be blank.
      const original = accountQuery.data?.account;
      const fields = [
        'legalFullName',
        'mobileNumber',
        'emergencyContactName',
        'emergencyContactPhone',
      ] as const;
      const body: Record<string, string | null> = {};
      for (const key of fields) {
        const next = values[key].trim();
        if (next.length > 0) {
          body[key] = next;
        } else if ((original?.[key] ?? '') !== '') {
          body[key] = null; // explicit clear of a previously-set value
        }
      }
      if (Object.keys(body).length === 0) return; // nothing to change
      await mutateJson('/api/users/me/account', body, { method: 'PATCH' });
    },
    onSuccess: () => {
      showToast({ kind: 'success', body: 'Identity details saved.' });
      queryClient.invalidateQueries({ queryKey: ['users', 'me', 'account'] });
    },
    onError: (err: unknown) => {
      showToast({ kind: 'error', body: saveErrorMessage(err) });
    },
  });

  const sendCode = useMutation({
    mutationFn: () =>
      mutateJson<{ ok: boolean; mock?: boolean }>('/api/verification/mobile/send', {
        mobile_number: identityForm.values.mobileNumber,
      }),
    onSuccess: (data) => {
      const mock = data.mock === true;
      setMockSend(mock);
      setVerifyStep('sent');
      showToast({
        kind: 'success',
        body: mock
          ? 'No text needed — enter any 6-digit code below.'
          : 'Verification code sent.',
      });
    },
    onError: (err: unknown) => {
      // AUD-020 — a 503 sms_unconfigured is a distinct, honest state (the
      // channel isn't switched on yet, not a transient failure) — the server
      // message already names the manual fallback, so surface it as-is rather
      // than folding it into the generic send-failure copy.
      if (err instanceof ApiError && err.status === 503) {
        showToast({ kind: 'error', body: err.message });
        return;
      }
      const message =
        err instanceof ApiError
          ? err.message
          : "We couldn't send that. Check your connection and try again.";
      showToast({ kind: 'error', body: message });
    },
  });

  const confirmCode = useMutation({
    mutationFn: () => mutateJson('/api/verification/mobile/verify', { code: verifyCode }),
    onSuccess: () => {
      setVerifyStep('idle');
      setVerifyCode('');
      showToast({ kind: 'success', body: 'Mobile number verified.' });
      queryClient.invalidateQueries({ queryKey: ['users', 'me', 'account'] });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof ApiError ? err.message : "We couldn't verify that. Check your connection and try again.";
      showToast({ kind: 'error', body: message });
    },
  });

  // FR-086: compute defensively. `data?.account.mobileVerifiedAt !== null`
  // resolves to `undefined !== null` → true while the query is loading or after
  // it errors (data never resolves), so an unverified user was shown the teal
  // "Verified" badge with no way to start SMS verification. Treat only a loaded
  // response with a non-null timestamp as verified; loading/error default to the
  // safe "not verified" reading that surfaces the Verify button.
  const mobileVerified =
    accountQuery.data !== undefined && accountQuery.data.account.mobileVerifiedAt !== null;

  return (
    <Card withBorder padding="lg" radius="md">
      <SectionTitle title="Identity and accountability" order={3} />
      <Text size="sm" c="dimmed" mt="xs" mb="md">
        Needed before a host can confirm your stay — add it now so nothing holds up your first exchange. Your legal name and emergency contact are shared with your host during a confirmed stay.
      </Text>
      <form
        onSubmit={identityForm.onSubmit((values) => {
          saveIdentity.mutate(values);
        })}
      >
        <Stack gap="md">
          <TextInput
            label="Legal full name"
            placeholder="As it appears on your ID"
            {...identityForm.getInputProps('legalFullName')}
          />
          <Box>
            <Group gap="sm" align="flex-end">
              <PhoneNumberInput
                label="Mobile number"
                description="Used for verification and emergency contact during stays."
                style={{ flex: 1, minWidth: 0 }}
                value={identityForm.values.mobileNumber}
                onChange={(v) => identityForm.setFieldValue('mobileNumber', v)}
                onBlur={() => identityForm.validateField('mobileNumber')}
                error={identityForm.errors['mobileNumber']}
              />
              {mobileVerified ? (
                <Badge color="teal" variant="light" size="lg" mb={2}>
                  Verified
                </Badge>
              ) : (
                <Button
                  variant="light"
                  size="sm"
                  mb={2}
                  disabled={!identityForm.values.mobileNumber || sendCode.isPending}
                  loading={sendCode.isPending}
                  onClick={() => {
                    // AUD-022 — catch an incomplete number here too, not just
                    // on form submit; this button never goes through
                    // identityForm.onSubmit.
                    const { hasError } = identityForm.validateField('mobileNumber');
                    if (hasError) return;
                    sendCode.mutate();
                  }}
                >
                  Verify
                </Button>
              )}
            </Group>
            {verifyStep === 'sent' && (
              <>
                <Group gap="sm" mt="sm">
                  <TextInput
                    placeholder="6-digit code"
                    maxLength={6}
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.currentTarget.value)}
                    style={{ width: 120 }}
                  />
                  <Button
                    size="sm"
                    disabled={verifyCode.length !== 6 || confirmCode.isPending}
                    loading={confirmCode.isPending}
                    onClick={() => confirmCode.mutate()}
                  >
                    Confirm
                  </Button>
                </Group>
                <Text size="xs" c="dimmed" mt="xs">
                  {mockSend
                    ? 'Text messages are off while we preview — any 6-digit code works here.'
                    : "Didn't get a text within a minute? Email hello@wwaave.com and we'll help."}
                </Text>
              </>
            )}
          </Box>
          <TextInput
            label="Emergency contact name"
            placeholder="Full name of your emergency contact"
            {...identityForm.getInputProps('emergencyContactName')}
          />
          <PhoneNumberInput
            label="Emergency contact phone"
            value={identityForm.values.emergencyContactPhone}
            onChange={(v) => identityForm.setFieldValue('emergencyContactPhone', v)}
            onBlur={() => identityForm.validateField('emergencyContactPhone')}
            error={identityForm.errors['emergencyContactPhone']}
          />
          <Button type="submit" loading={saveIdentity.isPending}>
            Save identity details
          </Button>
        </Stack>
      </form>
    </Card>
  );
}
